import { useEffect, useState } from "react";
import { Calendar, Pause, Play, RefreshCw, AlertCircle, Loader2, Users, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { adminEmailCalendar, adminPauseCustomerEmails, adminEmailCalendarPreview } from "../../lib/api";

/**
 * Email Calendar — shows every customer queued by the nurture cron streams
 * (cart_recovery, quote_followup, referral, in_production, winback_90,
 * winback_180) in one timeline. Admin can pause/resume individual customer
 * emails inline. Pause flag is stored in `email_opt_outs` and respected by
 * every nurture job (transactional + receipt emails are NOT affected).
 */
export default function EmailCalendarTab({ pw }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pausingEmail, setPausingEmail] = useState(null);
  const [preview, setPreview] = useState(null); // { stream, email, subject, html } | "loading"
  const [previewLoading, setPreviewLoading] = useState(false);

  const reload = () => {
    setLoading(true);
    adminEmailCalendar(pw)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load email calendar"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Defer initial fetch so we don't synchronously set state inside the
    // effect body (satisfies react-hooks/set-state-in-effect).
    queueMicrotask(reload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw]);

  const togglePause = async (email, currentlyPaused) => {
    setPausingEmail(email);
    try {
      await adminPauseCustomerEmails({ email, paused: !currentlyPaused }, pw);
      toast.success(currentlyPaused ? `Resumed emails for ${email}` : `Paused emails for ${email}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update pause status");
    } finally {
      setPausingEmail(null);
    }
  };

  const openPreview = async (streamKey, email) => {
    setPreviewLoading(true);
    setPreview({ loading: true, stream: streamKey, email });
    try {
      const res = await adminEmailCalendarPreview(streamKey, email, pw);
      setPreview(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email preview");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="surface-card p-12 text-center" data-testid="email-calendar-loading">
        <Loader2 className="animate-spin mx-auto text-zinc-500" size={28} />
      </div>
    );
  }
  if (!data) return null;

  const streamOrder = ["cart_recovery", "quote_followup", "referral", "in_production", "winback_90", "winback_180"];

  return (
    <div className="space-y-8" data-testid="email-calendar-tab">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
            <Calendar size={12} /> / EMAIL_CALENDAR
          </div>
          <h2 className="font-display text-3xl uppercase">Upcoming Nurture Emails</h2>
          <p className="font-body text-zinc-400 text-sm mt-2 max-w-2xl">
            Every customer currently queued by the nurture cron streams. Pause an individual customer to stop all marketing emails (transactional + receipt emails still send).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-display text-2xl text-white" data-testid="email-calendar-total">{data.total_queued || 0}</div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">QUEUED</div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl text-amber-400" data-testid="email-calendar-paused-count">{data.paused_count || 0}</div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">PAUSED</div>
          </div>
          <button
            onClick={reload}
            className="p-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]"
            aria-label="Refresh"
            data-testid="email-calendar-refresh"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </header>

      {data.total_queued === 0 && (
        <div className="surface-card p-12 text-center" data-testid="email-calendar-empty">
          <Users className="mx-auto text-zinc-600 mb-4" size={32} />
          <h3 className="font-display text-xl uppercase mb-2">Nothing queued</h3>
          <p className="font-body text-zinc-500 text-sm">No customers are currently due for a nurture email. Check back after the next cron run.</p>
        </div>
      )}

      {streamOrder.map((streamKey) => {
        const stream = data.streams?.[streamKey];
        if (!stream || stream.people.length === 0) return null;
        return (
          <section
            key={streamKey}
            className="surface-card p-6"
            data-testid={`email-stream-${streamKey}`}
          >
            <header className="flex justify-between items-start mb-4 pb-4 border-b border-[#222]">
              <div>
                <h3 className="font-display text-xl uppercase">{stream.label}</h3>
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">
                  {stream.schedule} · {stream.people.length} {stream.people.length === 1 ? "RECIPIENT" : "RECIPIENTS"}
                </div>
              </div>
              {stream.people.some((p) => p.paused) && (
                <div className="flex items-center gap-1.5 text-amber-400 font-mono text-[10px] tracking-widest">
                  <AlertCircle size={12} />
                  {stream.people.filter((p) => p.paused).length} PAUSED
                </div>
              )}
            </header>
            <div className="space-y-2">
              {stream.people.map((p) => (
                <div
                  key={`${streamKey}-${p.email}`}
                  className={`flex items-center justify-between gap-3 p-3 border ${p.paused ? "border-amber-700/40 bg-amber-900/10" : "border-[#1a1a1a] hover:border-[#333]"} text-sm`}
                  data-testid={`email-row-${streamKey}-${p.email}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-body text-zinc-200 font-medium">{p.name || p.email}</span>
                      <span className="font-mono text-[10px] tracking-widest text-zinc-500 truncate">{p.email}</span>
                      {p.paused && (
                        <span className="font-mono text-[9px] tracking-widest text-amber-400 border border-amber-700/40 px-1.5 py-0.5">PAUSED</span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] tracking-wider text-zinc-500 mt-1 truncate">{p.context}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openPreview(streamKey, p.email)}
                      className="px-3 py-1.5 border border-[#222] text-zinc-300 hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] font-mono text-[10px] tracking-widest flex items-center gap-1.5"
                      data-testid={`email-preview-${streamKey}-${p.email}`}
                      aria-label={`Preview email to ${p.email}`}
                    >
                      <Eye size={12} />
                      PREVIEW
                    </button>
                    <button
                      onClick={() => togglePause(p.email, p.paused)}
                      disabled={pausingEmail === p.email}
                      className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest flex items-center gap-1.5 disabled:opacity-50 ${p.paused ? "border-green-700/40 text-green-400 hover:bg-green-900/20" : "border-[#222] text-zinc-300 hover:border-amber-700/60 hover:text-amber-400"}`}
                      data-testid={`email-pause-toggle-${streamKey}-${p.email}`}
                    >
                      {pausingEmail === p.email
                        ? <Loader2 size={12} className="animate-spin" />
                        : p.paused ? <Play size={12} /> : <Pause size={12} />}
                      {p.paused ? "RESUME" : "PAUSE"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => !previewLoading && setPreview(null)}
          data-testid="email-preview-modal"
        >
          <div
            className="bg-[#0a0a0a] border border-[#222] w-full max-w-[720px] max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-4 p-5 border-b border-[#222]">
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">
                  / EMAIL_PREVIEW · {preview.stream}
                </div>
                <div className="font-display text-lg text-white truncate" data-testid="email-preview-subject">
                  {preview.loading ? "Loading…" : (preview.subject || "(no subject)")}
                </div>
                <div className="font-mono text-[10px] tracking-wider text-zinc-500 mt-1 truncate">
                  TO: {preview.email}
                </div>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="p-1.5 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] text-zinc-400 flex-shrink-0"
                data-testid="email-preview-close"
                aria-label="Close preview"
              >
                <X size={14} />
              </button>
            </header>
            <div className="flex-1 overflow-hidden bg-black">
              {preview.loading ? (
                <div className="flex items-center justify-center p-16">
                  <Loader2 className="animate-spin text-zinc-500" size={32} />
                </div>
              ) : (
                <iframe
                  title="Email preview"
                  srcDoc={preview.html}
                  sandbox=""
                  className="w-full h-[68vh] bg-white"
                  data-testid="email-preview-iframe"
                />
              )}
            </div>
            <footer className="px-5 py-3 border-t border-[#222] font-mono text-[10px] tracking-widest text-zinc-500 flex justify-between flex-wrap gap-2">
              <span>RENDERED FROM LIVE CUSTOMER DATA</span>
              <span>SANDBOXED · NO SCRIPTS</span>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
